/**
 * useDocumentEdgeDetectionML
 *
 * ML-based real-time document edge detection using DocAligner (TFLite) via
 * TFLite Web Runtime. Detects the 4 corners of a document in the camera feed
 * for perspective correction, quality gating, and auto-capture.
 *
 * This is the "enhanced experience" counterpart to useDocumentEdgeDetection
 * (which uses canvas-based 1D Otsu brightness profiling). When the TFLite
 * runtime + model are available, this hook provides ML-based 4-corner
 * detection that is robust to shadows, low contrast, and textured
 * backgrounds. When unavailable, callers should fall back to the canvas-based
 * hook.
 *
 * Architecture:
 *  - Singleton TFLite model runner per browsing context (no double-init)
 *  - TFLite Web Runtime with vanilla WASM (no SIMD required)
 *  - Raw WASM API client loaded via URL (bypasses Turbopack bundling issues)
 *  - requestAnimationFrame detection loop with interval throttling
 *  - Temporal EMA smoothing on corner positions
 *  - Ref-based state to avoid re-creating callbacks
 *
 * Model: DocAligner heatmap regression (lcnet100 variant, ~4.8MB TFLite)
 *  Input:  (1, 256, 256, 3) float32 — NHWC, normalized to [0, 1]
 *  Output: (1, 128, 128, 4) float32 — one heatmap channel per corner
 *          (TL, TR, BR, BL). Each channel is thresholded; the largest
 *          connected blob's intensity-weighted centroid becomes the corner.
 *
 * @author TotalAccess Development Team
 * @date September 2026
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { Point } from '@/lib/perspectiveTransform';

// ==================== TYPES ====================

export interface DocumentEdgeML extends DocumentEdge {
  /** 4 detected corners in relative coordinates (0–1), clockwise from top-left.
   *  Null when no document is detected. */
  corners: Point[] | null;
  /** Per-corner confidence values (0–1) from heatmap peaks.
   *  Order: TL, TR, BR, BL. Null when no document is detected. */
  cornerConfidences: number[] | null;
  /** Blur score from Laplacian variance (0–1, higher = sharper). */
  sharpness: number;
  /** Whether the TFLite model is loaded and running. */
  modelReady: boolean;
}

// Re-export DocumentEdge for convenience
export type { DocumentEdge } from '@/hooks/useDocumentEdgeDetection';
import type { DocumentEdge } from '@/hooks/useDocumentEdgeDetection';

export interface UseDocumentEdgeDetectionMLConfig {
  /** Sampling interval in ms (default: 200 — ~5fps, balances accuracy and battery) */
  intervalMs?: number;
  /** Smoothing factor for temporal EMA on corner positions (0–1, default: 0.4) */
  smoothing?: number;
  /** Heatmap binarization threshold (default: 0.3) */
  heatmapThreshold?: number;
  /** Whether detection is enabled (default: true) */
  enabled?: boolean;
  /** TFLite model URL (default: '/tflite/docaligner.tflite') */
  modelUrl?: string;
  /** TFLite WASM runtime path (default: '/tflite/wasm/') */
  wasmPath?: string;
  /** TFLite Web API client script URL (default: '/tflite/wasm/tflite_web_api_client.js') */
  apiClientUrl?: string;
  /** Minimum confidence to accept a detection (default: 0.3) */
  minConfidence?: number;
}

// ==================== CAPABILITY DETECTION ====================

/**
 * Check if this device/browser can run TFLite Web Runtime.
 * TFLite Web has a vanilla (non-SIMD) WASM build, so we only need
 * basic WebAssembly support — no SIMD or WebGPU required.
 */
let capabilityCache: Promise<boolean> | null = null;
async function checkTFLiteCapability(): Promise<boolean> {
  if (capabilityCache !== null) return capabilityCache;

  capabilityCache = (async (): Promise<boolean> => {
    // TFLite Web Runtime only needs basic WebAssembly support.
    // It has a vanilla (non-SIMD) WASM build that works on all modern browsers,
    // including Samsung S24 Capacitor WebView which doesn't support WASM SIMD.
    // The face-api TF.js WASM backend already proves WASM works on this device.
    // Note: WebAssembly.validate() can return false on some WebViews even when
    // WASM instantiation works — don't use it as a gate.
    if (typeof WebAssembly === 'undefined' || typeof WebAssembly.Module === 'undefined') {
      console.log('[DocEdgeML] WebAssembly not available — ML detection disabled');
      return false;
    }

    console.log('[DocEdgeML] WebAssembly available — TFLite ML detection enabled');
    return true;
  })();

  return capabilityCache;
}

// ==================== TFLITE RAW API ====================

/**
 * The raw TFLite Web API client (Closure Library bundle, no tfjs dependency).
 * Loaded via URL import to bypass Turbopack bundling issues.
 */
interface TFLiteWebAPI {
  tflite_web_api: {
    setWasmPath(path: string): void;
    getWasmFeatures(): Promise<{ simd: boolean; multiThreading: boolean }>;
  };
  TFLiteWebModelRunner: {
    create(
      model: string | ArrayBuffer,
      options?: { numThreads?: number; enableProfiling?: boolean },
    ): Promise<TFLiteModelRunner>;
  };
}

interface TFLiteModelRunner {
  getInputs(): TFLiteTensorInfo[];
  getOutputs(): TFLiteTensorInfo[];
  infer(): boolean;
  clean(): void;
}

interface TFLiteTensorInfo {
  id: number;
  dataType: string;
  name: string;
  shape: string;
  data(): Float32Array | Int32Array | Uint8Array;
}

/** Cached API client singleton — loaded once per browsing context. */
let apiClientSingleton: Promise<TFLiteWebAPI> | null = null;

async function loadTFLiteAPI(apiClientUrl: string): Promise<TFLiteWebAPI> {
  if (apiClientSingleton) return apiClientSingleton;

  apiClientSingleton = (async (): Promise<TFLiteWebAPI> => {
    // The tflite_web_api_client.js is a Closure Library bundle that uses
    // goog.exportSymbol to expose its API as window.tfweb.
    // It also uses CommonJS `exports` at the bottom of the file, which is
    // not defined in browser context. We polyfill it before loading.
    const w = window as unknown as Record<string, unknown>;
    if (typeof w.exports === 'undefined') {
      w.exports = {};
    }
    if (typeof w.module === 'undefined') {
      w.module = { exports: w.exports };
    }

    return new Promise<TFLiteWebAPI>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = apiClientUrl;
      script.onload = () => {
        const win = window as unknown as { tfweb?: TFLiteWebAPI };
        if (win.tfweb) {
          resolve(win.tfweb);
          return;
        }
        reject(new Error('TFLite Web API client loaded but window.tfweb not found'));
      };
      script.onerror = () => reject(new Error(`Failed to load TFLite Web API client from ${apiClientUrl}`));
      document.head.appendChild(script);
    });
  })();

  try {
    return await apiClientSingleton;
  } catch (err) {
    apiClientSingleton = null;
    throw err;
  }
}

// ==================== TFLITE MODEL SINGLETON ====================

/** Prevents double-initialization across hook instances. */
let modelRunnerSingleton: Promise<TFLiteModelSession> | null = null;
let modelRunnerUrl: string | null = null;

interface TFLiteModelSession {
  runner: TFLiteModelRunner;
  inputTensor: TFLiteTensorInfo;
  outputTensor: TFLiteTensorInfo;
  inputData: Float32Array;
  outputData: Float32Array;
  inputShape: number[];
  outputShape: number[];
  _debugCounter?: number;
}

/**
 * Initialize the TFLite model runner (singleton per browsing context).
 * Uses the raw WASM API — no tfjs dependency.
 */
async function initTFLiteSession(
  modelUrl: string,
  wasmPath: string,
  apiClientUrl: string,
): Promise<TFLiteModelSession> {
  const trimmedModelUrl = modelUrl.trim();
  const trimmedWasmPath = wasmPath.endsWith('/') ? wasmPath : wasmPath + '/';

  // Return existing singleton if model URL matches
  if (modelRunnerSingleton && modelRunnerUrl === trimmedModelUrl) {
    return modelRunnerSingleton;
  }

  if (modelRunnerSingleton && modelRunnerUrl !== trimmedModelUrl) {
    try {
      const old = await modelRunnerSingleton;
      old.runner.clean();
    } catch { /* ignore */ }
    modelRunnerSingleton = null;
    modelRunnerUrl = null;
  }

  modelRunnerUrl = trimmedModelUrl;
  modelRunnerSingleton = (async (): Promise<TFLiteModelSession> => {
    const api = await loadTFLiteAPI(apiClientUrl);
    api.tflite_web_api.setWasmPath(trimmedWasmPath);

    // Fetch model bytes
    const modelResponse = await fetch(trimmedModelUrl);
    if (!modelResponse.ok) {
      throw new Error(`Failed to fetch DocAligner TFLite model: ${modelResponse.status}`);
    }
    const modelBuffer = await modelResponse.arrayBuffer();

    // Create model runner
    const runner = await api.TFLiteWebModelRunner.create(modelBuffer, {
      numThreads: 1, // Single-threaded for compatibility
    });

    const inputs = runner.getInputs();
    const outputs = runner.getOutputs();
    if (!inputs.length || !outputs.length) {
      throw new Error('TFLite model has unexpected I/O structure');
    }

    const inputTensor = inputs[0];
    const outputTensor = outputs[0];
    const inputData = inputTensor.data();
    const outputData = outputTensor.data();

    if (!(inputData instanceof Float32Array) || !(outputData instanceof Float32Array)) {
      throw new Error(`TFLite model expects float32 I/O, got input: ${inputTensor.dataType}, output: ${outputTensor.dataType}`);
    }

    const inputShape = inputTensor.shape.split(',').map(Number);
    const outputShape = outputTensor.shape.split(',').map(Number);

    console.log(`[DocEdgeML] TFLite model loaded — input: [${inputShape}], output: [${outputShape}]`);

    // Test inference with zeros
    inputData.fill(0);
    runner.infer();
    console.log(`[DocEdgeML] TFLite test inference OK — output range: [${outputData[0]}, ${outputData[outputData.length - 1]}]`);

    return { runner, inputTensor, outputTensor, inputData, outputData, inputShape, outputShape };
  })();

  try {
    return await modelRunnerSingleton;
  } catch (err) {
    modelRunnerSingleton = null;
    modelRunnerUrl = null;
    throw err;
  }
}

// ==================== PREPROCESSING ====================

const MODEL_INPUT_SIZE = 256;

/**
 * Extract a frame from the video element, downscale to 256x256, and convert
 * to NHWC float32 tensor normalized to [0, 1].
 *
 * The video frame is stretched to 256x256 (no aspect-ratio preservation).
 * The DocAligner model was trained on stretched input, so corner predictions
 * are in [0,1] relative to the full frame and need no remapping.
 *
 * TFLite uses NHWC (1, Height, Width, Channels) format.
 */
function preprocessVideoFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): { tensorData: Float32Array; videoWidth: number; videoHeight: number } | null {
  const videoWidth = video.videoWidth;
  const videoHeight = video.videoHeight;
  if (!videoWidth || !videoHeight) return null;

  canvas.width = MODEL_INPUT_SIZE;
  canvas.height = MODEL_INPUT_SIZE;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  try {
    ctx.drawImage(video, 0, 0, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);
  } catch {
    return null;
  }

  let imageData: ImageData;
  try {
    imageData = ctx.getImageData(0, 0, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);
  } catch {
    return null;
  }

  // Convert RGBA → NHWC float32, normalized to [0, 1]
  const pixelData = imageData.data;
  const tensorData = new Float32Array(1 * MODEL_INPUT_SIZE * MODEL_INPUT_SIZE * 3);

  for (let i = 0, j = 0; i < pixelData.length; i += 4, j += 3) {
    tensorData[j] = pixelData[i] / 255.0;
    tensorData[j + 1] = pixelData[i + 1] / 255.0;
    tensorData[j + 2] = pixelData[i + 2] / 255.0;
  }

  return { tensorData, videoWidth, videoHeight };
}

// ==================== POSTPROCESSING ====================

/**
 * Decode the heatmap output from DocAligner into 4 corner positions.
 *
 * The TFLite output tensor has shape (1, H, W, 4) — NHWC format.
 * Each channel is a heatmap for one corner (TL, TR, BR, BL).
 */
const DECODE_RADIUS = 4;

function decodeHeatmaps(
  heatmapData: Float32Array,
  heatmapHeight: number,
  heatmapWidth: number,
  numChannels: number,
  threshold: number,
): { corners: Point[]; confidences: number[] } | null {
  const corners: Point[] = [];
  const confidences: number[] = [];

  for (let c = 0; c < numChannels; c++) {
    // Stage 1: Find the argmax (peak) location
    let maxVal = -1;
    let maxX = 0;
    let maxY = 0;
    for (let y = 0; y < heatmapHeight; y++) {
      for (let x = 0; x < heatmapWidth; x++) {
        const val = heatmapData[(y * heatmapWidth + x) * numChannels + c];
        if (val > maxVal) {
          maxVal = val;
          maxX = x;
          maxY = y;
        }
      }
    }

    if (maxVal < threshold) {
      return null;
    }

    confidences.push(maxVal);

    // Stage 2: Intensity-weighted centroid around the peak
    const localThreshold = threshold * maxVal;
    let sumWeight = 0;
    let sumX = 0;
    let sumY = 0;

    const xStart = Math.max(0, maxX - DECODE_RADIUS);
    const xEnd = Math.min(heatmapWidth - 1, maxX + DECODE_RADIUS);
    const yStart = Math.max(0, maxY - DECODE_RADIUS);
    const yEnd = Math.min(heatmapHeight - 1, maxY + DECODE_RADIUS);

    for (let y = yStart; y <= yEnd; y++) {
      for (let x = xStart; x <= xEnd; x++) {
        const val = heatmapData[(y * heatmapWidth + x) * numChannels + c];
        if (val >= localThreshold) {
          sumWeight += val;
          sumX += x * val;
          sumY += y * val;
        }
      }
    }

    if (sumWeight === 0) {
      corners.push({ x: maxX / heatmapWidth, y: maxY / heatmapHeight });
      continue;
    }

    corners.push({
      x: (sumX / sumWeight) / heatmapWidth,
      y: (sumY / sumWeight) / heatmapHeight,
    });
  }

  return { corners, confidences };
}

function cornersToEdge(corners: Point[], confidence: number): DocumentEdge {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of corners) {
    if (c.x < minX) minX = c.x;
    if (c.y < minY) minY = c.y;
    if (c.x > maxX) maxX = c.x;
    if (c.y > maxY) maxY = c.y;
  }
  const width = maxX - minX;
  const height = maxY - minY;
  return {
    x: minX,
    y: minY,
    width,
    height,
    confidence,
    aspectRatio: width / Math.max(height, 0.001),
  };
}

/**
 * Compute Laplacian variance as a blur metric.
 * Higher variance = sharper image. Returns 0–1 (normalized).
 */
function computeSharpness(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): number {
  const sampleSize = 64;
  canvas.width = sampleSize;
  canvas.height = sampleSize;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return 0;

  try {
    ctx.drawImage(video, 0, 0, sampleSize, sampleSize);
  } catch {
    return 0;
  }

  let imageData: ImageData;
  try {
    imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
  } catch {
    return 0;
  }

  const data = imageData.data;
  const gray = new Float32Array(sampleSize * sampleSize);

  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    gray[j] = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
  }

  let sumSq = 0;
  let count = 0;
  for (let y = 1; y < sampleSize - 1; y++) {
    for (let x = 1; x < sampleSize - 1; x++) {
      const idx = y * sampleSize + x;
      const lap = -4 * gray[idx]
        + gray[idx - 1]
        + gray[idx + 1]
        + gray[idx - sampleSize]
        + gray[idx + sampleSize];
      sumSq += lap * lap;
      count++;
    }
  }

  const variance = count > 0 ? sumSq / count : 0;
  const normalized = variance / (variance + 200);
  return Math.min(1, normalized);
}

// ==================== HOOK ====================

export function useDocumentEdgeDetectionML(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  config: UseDocumentEdgeDetectionMLConfig = {},
) {
  const {
    intervalMs = 200,
    smoothing = 0.4,
    heatmapThreshold = 0.3,
    enabled = true,
    modelUrl = '/tflite/docaligner.tflite',
    wasmPath = '/tflite/wasm/',
    apiClientUrl = '/tflite/wasm/tflite_web_api_client.js',
    minConfidence = 0.3,
  } = config;

  const [documentEdge, setDocumentEdge] = useState<DocumentEdgeML | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<TFLiteModelSession | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastSampleRef = useRef(0);
  const enabledRef = useRef(enabled);
  const configRef = useRef({ smoothing, heatmapThreshold, minConfidence, intervalMs });
  const smoothedCornersRef = useRef<Point[] | null>(null);
  const consecutiveMissesRef = useRef(0);
  const initInFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    enabledRef.current = enabled;
    configRef.current = { smoothing, heatmapThreshold, minConfidence, intervalMs };
  }, [enabled, smoothing, heatmapThreshold, minConfidence, intervalMs]);

  const initialize = useCallback(async (): Promise<void> => {
    if (sessionRef.current || initInFlightRef.current) return;

    initInFlightRef.current = (async (): Promise<void> => {
      try {
        setInitError(null);

        const capable = await checkTFLiteCapability();
        if (!capable) {
          console.log('[DocEdgeML] Device does not support TFLite Web Runtime — staying in fallback mode');
          setInitError('TFLite Runtime not supported on this device');
          setIsInitialized(false);
          return;
        }

        console.log('[DocEdgeML] Initializing TFLite session...');
        const session = await initTFLiteSession(modelUrl, wasmPath, apiClientUrl);
        sessionRef.current = session;
        setIsInitialized(true);
        console.log('[DocEdgeML] TFLite session ready');
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[DocEdgeML] Failed to initialize TFLite session:', msg);
        setInitError(msg);
        setIsInitialized(false);
      } finally {
        initInFlightRef.current = null;
      }
    })();

    await initInFlightRef.current;
  }, [modelUrl, wasmPath, apiClientUrl]);

  const detectOnce = useCallback(async (): Promise<DocumentEdgeML | null> => {
    const video = videoRef.current;
    const session = sessionRef.current;
    if (!video || !session) return null;

    const { videoWidth, videoHeight } = { videoWidth: video.videoWidth, videoHeight: video.videoHeight };
    if (!videoWidth || !videoHeight) return null;

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const canvas = canvasRef.current;

    // Preprocess: extract and normalize frame (NHWC format)
    const preprocessed = preprocessVideoFrame(video, canvas);
    if (!preprocessed) return null;

    // Copy preprocessed data into the model's input buffer
    session.inputData.set(preprocessed.tensorData);

    // Run inference
    let success: boolean;
    try {
      success = session.runner.infer();
    } catch {
      return null;
    }
    if (!success) return null;

    // Read output from the model's output buffer
    const outputData = session.outputData;
    const outputShape = session.outputShape;
    const heatmapHeight = outputShape[1] || 128;
    const heatmapWidth = outputShape[2] || 128;
    const numChannels = outputShape[3] || 4;

    // Debug: log heatmap peak values per channel (every 30th detection)
    if (!session._debugCounter) session._debugCounter = 0;
    session._debugCounter++;
    if (session._debugCounter % 30 === 0) {
      const peaks: number[] = [];
      for (let c = 0; c < numChannels; c++) {
        let maxVal = 0;
        for (let i = 0; i < outputData.length; i += numChannels) {
          if (outputData[i + c] > maxVal) maxVal = outputData[i + c];
        }
        peaks.push(maxVal);
      }
      console.log(`[DocEdgeML] Heatmap peaks (count=${session._debugCounter}): [${peaks.map(v => v.toFixed(4)).join(', ')}]`);
    }

    // Decode heatmaps to corners
    const { heatmapThreshold: thresh } = configRef.current;
    const decoded = decodeHeatmaps(outputData, heatmapHeight, heatmapWidth, numChannels, thresh);
    if (!decoded) {
      return null;
    }
    const { corners, confidences: cornerConfidences } = decoded;

    // Compute overall confidence from per-corner confidences
    const confidence = Math.min(1, cornerConfidences.reduce((a, b) => a + b, 0) / numChannels);

    // Temporal smoothing on corner positions
    const { smoothing: smooth } = configRef.current;
    const prev = smoothedCornersRef.current;
    if (prev && prev.length === 4) {
      const smoothed: Point[] = corners.map((c, i) => ({
        x: prev[i].x + (c.x - prev[i].x) * (1 - smooth),
        y: prev[i].y + (c.y - prev[i].y) * (1 - smooth),
      }));
      smoothedCornersRef.current = smoothed;
      corners.splice(0, 4, ...smoothed);
    } else {
      smoothedCornersRef.current = corners;
    }

    // Compute sharpness (blur metric)
    const sharpness = computeSharpness(video, canvas);

    // Build DocumentEdge from corners
    const edge = cornersToEdge(corners, confidence);

    return {
      ...edge,
      corners,
      cornerConfidences,
      sharpness,
      modelReady: true,
    };
  }, [videoRef]);

  const tick = useCallback(() => {
    rafRef.current = requestAnimationFrame(tick);

    if (!enabledRef.current || !sessionRef.current) return;

    const now = performance.now();
    if (now - lastSampleRef.current < configRef.current.intervalMs) return;
    lastSampleRef.current = now;

    void detectOnce().then((result) => {
      if (result && result.confidence >= configRef.current.minConfidence) {
        consecutiveMissesRef.current = 0;
        setDocumentEdge(result);
      } else {
        consecutiveMissesRef.current++;
        // With the hybrid approach, ML runs at 500ms intervals and only
        // provides corners for perspective correction at capture time.
        // Keep the last successful detection longer (6 misses = ~3s at 500ms)
        // so that a brief detection gap doesn't clear corners right when
        // the user taps capture.
        if (consecutiveMissesRef.current >= 6) {
          smoothedCornersRef.current = null;
          setDocumentEdge(null);
        }
      }
    });
  }, [detectOnce]);

  useEffect(() => {
    if (!enabled) return;
    void initialize();
  }, [enabled, initialize]);

  useEffect(() => {
    if (!enabled || !isInitialized) return;

    console.log('[DocEdgeML] Starting detection loop');
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      smoothedCornersRef.current = null;
      consecutiveMissesRef.current = 0;
      setDocumentEdge(null);
    };
  }, [enabled, isInitialized, tick]);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return {
    documentEdge,
    isInitialized,
    initError,
    initialize,
  };
}
